import { useTheme } from '@mui/material'
import React, { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetProjectMapSVG } from 'src/api/project'
import { CenteredSpinner } from 'src/components/Spinner'
import { IPosition, IPositionType } from 'src/generated-types'
import {
  ConfigurationCard,
  ConfigureView,
  PositionConfig,
  QuickSelectMode,
  ZoomRequest,
} from 'src/generated-types/configure-positions'
import { DrawingColumn, DrawingViewport, SvgHost } from './ConfigurePositionsDrawing.style'
import { getConfigurePalette } from './configurePalette'
import { isUngroupedName } from './configurePositions.action'
import { positionLabel } from './positionLabels'
import DrawingHints from './DrawingHints'
import DrawingToolbar from './DrawingToolbar'
import DrawingTooltip, { DrawingTooltipHandle } from './DrawingTooltip'
import PositionListView from './PositionListView'
import { setHoveredPosition } from './drawingSvgElements'
import { useDrawingSelection } from './useDrawingSelection'
import { useDrawingPanZoom } from './useDrawingPanZoom'
import { useDrawingSvgPaint } from './useDrawingSvgPaint'

interface ConfigurePositionsDrawingProps {
  customerId: EntityId
  localityId: EntityId
  projectId: EntityId
  view: ConfigureView
  zoom: number
  onView: (view: ConfigureView) => void
  onZoom: (request: ZoomRequest) => void
  activeType: IPositionType
  typeLabel: string
  positions: IPosition[]
  allPositions: IPosition[]
  cages: IPosition[]
  configById: Map<number, PositionConfig>
  cards: ConfigurationCard[]
  highlightedIds: Set<number>
  onHoverPosition: (id: number | null) => void
  selectedIds: Set<number>
  onPositionClick: (id: number) => void
  onAdd: (ids: number[]) => void
  onRemove: (ids: number[]) => void
  onToggleGroup: (ids: number[]) => void
  onQuickSelect: (mode: QuickSelectMode) => void
}

const ConfigurePositionsDrawing: React.FC<ConfigurePositionsDrawingProps> = ({
  customerId,
  localityId,
  projectId,
  view,
  zoom,
  onView,
  onZoom,
  activeType,
  typeLabel,
  positions,
  allPositions,
  cages,
  configById,
  cards,
  highlightedIds,
  onHoverPosition,
  selectedIds,
  onPositionClick,
  onAdd,
  onRemove,
  onToggleGroup,
  onQuickSelect,
}) => {
  const theme = useTheme()
  const { t } = useTranslation(['position', 'component'])
  const palette = useMemo(() => getConfigurePalette(theme), [theme])
  const { data: svg, isLoading } = useGetProjectMapSVG(
    customerId,
    localityId,
    projectId,
    !!customerId && !!localityId && !!projectId,
  )
  const columnRef = useRef<HTMLDivElement>(null)
  const viewportRef = useRef<HTMLDivElement>(null)
  const hostRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<DrawingTooltipHandle>(null)
  const isDrawingView = view === 'drawing'

  const svgPaint = useDrawingSvgPaint({
    hostRef,
    svg,
    positions: allPositions,
    cages,
    activeType,
    selectedIds,
    configById,
    highlightedIds,
    palette,
  })
  const panZoom = useDrawingPanZoom({ view, zoom, onZoom, hostRef, viewportRef })
  const selection = useDrawingSelection({
    hostRef,
    positionShapesRef: svgPaint.positionShapesRef,
    cageShapesRef: svgPaint.cageShapesRef,
    marqueeRef: svgPaint.marqueeRef,
    activeType,
    typeLabel,
    palette,
    onAdd,
    onRemove,
    onToggleGroup,
  })

  const setHover = (positionId: string | null) => {
    const host = hostRef.current
    if (!host || svgPaint.hoveredIdRef.current === positionId) return
    svgPaint.hoveredIdRef.current = positionId
    setHoveredPosition(host, positionId)
    onHoverPosition(positionId ? Number(positionId) : null)
  }

  const clearHover = () => {
    setHover(null)
    tooltipRef.current?.hide()
  }

  const showPositionTooltip = (event: React.PointerEvent, positionId: number) => {
    const position = allPositions.find(item => item.id === positionId)
    const config = configById.get(positionId)
    const details = [
      config?.drawingNumber
        ? `${t('drawingNo', { ns: 'component' })}: ${config.drawingNumber}`
        : t('no-drawing', { ns: 'position' }),
    ]
    if (config?.groupName && !isUngroupedName(config.groupName)) {
      details.push(`${t('group', { ns: 'position' })}: ${config.groupName}`)
    }
    tooltipRef.current?.show(event, position ? positionLabel(position) : String(positionId), details.join('\n'))
  }

  const onPointerDown = (event: React.PointerEvent) => {
    if (!isDrawingView) return
    if (panZoom.startPan(event)) {
      tooltipRef.current?.hide()
      return
    }
    if (selection.startMarquee(event)) clearHover()
  }

  const onPointerMove = (event: React.PointerEvent) => {
    if (panZoom.movePan(event) || selection.moveMarquee(event) || !isDrawingView) return
    const target = (event.target as Element).closest?.('[data-cfg-id]')
    const positionType = Number(target?.getAttribute('data-cfg-type'))
    if (!target || positionType === IPositionType.Cage) {
      clearHover()
      return
    }
    const positionId = Number(target.getAttribute('data-cfg-id'))
    setHover(positionType === activeType ? String(positionId) : null)
    showPositionTooltip(event, positionId)
  }

  const onPointerUp = (event: React.PointerEvent) => {
    const pan = panZoom.endPan()
    if (pan) {
      if (pan.moved) selection.suppressNextClick()
      return
    }
    selection.endMarquee(event)
  }

  const onClick = (event: React.MouseEvent) => {
    if (panZoom.spaceHeldRef.current) return
    const positionId = selection.resolveClick(event)
    if (positionId != null) onPositionClick(positionId)
  }

  const preventInDrawing = (event: React.SyntheticEvent) => {
    if (isDrawingView) event.preventDefault()
  }

  const cursorMode = !isDrawingView
    ? 'default'
    : panZoom.isPanning
    ? 'grabbing'
    : panZoom.isHandMode
    ? 'grab'
    : 'default'

  return (
    <DrawingColumn ref={columnRef}>
      <DrawingToolbar
        view={view}
        zoom={zoom}
        palette={palette}
        onView={onView}
        onQuickSelect={onQuickSelect}
        onZoom={panZoom.zoomFromCenter}
      />
      <DrawingViewport
        ref={viewportRef}
        cursorMode={cursorMode}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => {
          panZoom.endPan()
          selection.cancelMarquee()
        }}
        onPointerEnter={panZoom.onPointerEnter}
        onPointerLeave={() => {
          panZoom.onPointerLeave()
          clearHover()
        }}
        onClick={onClick}
        onMouseDown={event => {
          if (event.button === 1 || event.button === 2) preventInDrawing(event)
        }}
        onContextMenu={preventInDrawing}
        onAuxClick={preventInDrawing}
      >
        {isLoading && <CenteredSpinner />}
        <SvgHost ref={hostRef} zoom={zoom} visible={isDrawingView} dangerouslySetInnerHTML={{ __html: svg || '' }} />
        {view === 'list' && (
          <PositionListView
            positions={positions}
            cards={cards}
            configById={configById}
            selectedIds={selectedIds}
            onPositionClick={onPositionClick}
            onAdd={onAdd}
          />
        )}
      </DrawingViewport>
      <DrawingHints view={view} activeType={activeType} palette={palette} />
      <DrawingTooltip ref={tooltipRef} containerRef={columnRef} />
    </DrawingColumn>
  )
}

export default ConfigurePositionsDrawing
